import { Box } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import { useRef, useState, type ReactNode } from "react";

/**
 * A RecordCard you can swipe to act on.
 *
 * DELIBERATELY NARROWER THAN THE BRIEF ASKED FOR, and the reason matters.
 *
 * The design has "swipe left to advance, swipe right to send back" on every
 * job card. In this app most stage changes cannot be done by a gesture:
 * diagnosing needs an outcome and findings, completing needs the battery
 * going out and sometimes a pro-rata amount, and each step is gated to a
 * different role. A swipe that opened a form would be a worse affordance than
 * a button - it promises a one-gesture action and then asks four questions.
 *
 * So a swipe action is OPTIONAL on both sides, and a card renders as an
 * ordinary card when the caller supplies none. Only pass one where a single,
 * permitted, no-input action genuinely exists.
 *
 * Both actions must ALSO be reachable without swiping. A gesture is not an
 * affordance for someone using a keyboard, a screen reader, or one hand
 * holding a battery - the caller is expected to keep the same action in the
 * card's own controls, and the demo route shows that pairing.
 */
export function SwipeableCard({
  children,
  onSwipeLeft,
  onSwipeRight,
  leftLabel = "Advance",
  rightLabel = "Send back",
}: {
  children: ReactNode;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  leftLabel?: string;
  rightLabel?: string;
}) {
  const reduced = useReducedMotion();
  const [dx, setDx] = useState(0);
  const start = useRef<number | null>(null);
  const armed = Boolean(onSwipeLeft || onSwipeRight);

  /** Far enough to be a decision rather than a scroll. */
  const THRESHOLD = 72;

  function end() {
    if (start.current === null) return;
    if (dx <= -THRESHOLD && onSwipeLeft) onSwipeLeft();
    if (dx >= THRESHOLD && onSwipeRight) onSwipeRight();
    start.current = null;
    setDx(0);
  }

  if (!armed) return <>{children}</>;

  return (
    <Box style={{ position: "relative", overflow: "hidden", borderRadius: 8 }}>
      {/* What the swipe will do, revealed underneath. Colour AND words. */}
      <Box
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: dx < 0 ? "flex-end" : "flex-start",
          padding: "0 var(--mantine-spacing-lg)",
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "var(--mantine-color-white)",
          background:
            dx < 0
              ? "var(--mantine-color-teal-7)"
              : "var(--mantine-color-gray-6)",
          opacity: Math.min(1, Math.abs(dx) / THRESHOLD),
        }}
      >
        {dx < 0 ? leftLabel : rightLabel}
      </Box>

      <Box
        onPointerDown={(event) => {
          start.current = event.clientX;
        }}
        onPointerMove={(event) => {
          if (start.current === null) return;
          const next = event.clientX - start.current;
          // Only offer the direction the caller actually wired up.
          if (next < 0 && !onSwipeLeft) return;
          if (next > 0 && !onSwipeRight) return;
          setDx(next);
        }}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
        style={{
          transform: `translateX(${dx}px)`,
          // prefers-reduced-motion: the card still moves under the finger,
          // because that IS the gesture, but it snaps back instantly rather
          // than gliding.
          transition: start.current === null && !reduced ? "transform 150ms ease" : "none",
          position: "relative",
          touchAction: "pan-y",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
